import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { formatFamilyMoney } from '../../../components/notification-modal/utils/notification-family-sale.util';
import type { FamilyMemberErrors } from '../../../components/notification-modal/utils/notification-family-sale.util';
import type {
  FamilyFormOption,
  FamilyFormPrice,
  FamilyMemberFormModel,
} from '../../types/family-member-form.types';
import {
  CUSTOMER_PHONE_MAX_LENGTH,
  sanitizeCustomerPhoneInput,
} from '../../validators/customer-contact-field.rules';

export type FamilyFormFieldMode = 'edit' | 'readonly' | 'hidden';

/**
 * The family member fields: name, phone, plate with its state, package and discount, with the
 * price under them. Presentational: the host owns the model and its errors (it edits the object
 * it is given), builds the option lists and decides what to do on save. Shared by the Order
 * Request "Sell as Family Plan" block, the add-members pop-up and the Family Plan tab's edit form.
 *
 * @selector app-family-member-form
 * @input model - The values being edited (mutated in place).
 * @input errors - Field errors from `validateFamilyMember`.
 * @input idPrefix - Prefix of the element ids, unique per form on the page.
 * @input nameMode - 'split' for first and last name, 'full' for one "Full Name" field.
 * @input packageMode - Package: editable, shown read-only, or left out.
 * @input discountMode - Discount: editable, shown read-only, or left out.
 * @input testIdPrefix - When set, adds `<prefix>-plate/-package/-discount/-new-form/-new-price` test ids.
 */
@Component({
  selector: 'app-family-member-form',
  standalone: true,
  imports: [FormsModule, MatSelectModule],
  templateUrl: './family-member-form.component.html',
  styleUrls: ['./family-member-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class FamilyMemberFormComponent {
  @Input({ required: true }) model!: FamilyMemberFormModel;
  @Input() errors: FamilyMemberErrors = {};
  @Input() idPrefix = 'family-member';
  @Input() testIdPrefix: string | null = null;
  @Input() disabled = false;
  @Input() nameMode: 'split' | 'full' = 'split';
  @Input() packageMode: FamilyFormFieldMode = 'edit';
  @Input() discountMode: FamilyFormFieldMode = 'edit';
  @Input() states: readonly { code: string; name: string }[] = [];
  @Input() packages: FamilyFormOption[] = [];
  @Input() discounts: FamilyFormOption<number>[] = [];
  /** Name and phone are fixed (a member without a phone of their own shares the primary's). */
  @Input() contactLocked = false;
  @Input() price: FamilyFormPrice | null = null;
  @Input() priceCaption = 'Price';
  /** Appended to the price value; the add forms show "/mo", one shows a bare charge. */
  @Input() priceUnit = '/mo';
  /** The struck-through list price next to the discounted one. */
  @Input() showListPrice = true;

  readonly phoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;
  readonly money = formatFamilyMoney;

  testId(suffix: string): string | null {
    return this.testIdPrefix ? `${this.testIdPrefix}-${suffix}` : null;
  }

  /** Same input rule as the Order Request phone field: digits only, at most 10. */
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.model.phone = sanitizeCustomerPhoneInput(input.value);
    input.value = this.model.phone;
  }

  optionLabel<T>(options: FamilyFormOption<T>[], value: T | null): string {
    return options.find(option => option.value === value)?.label ?? '';
  }
}
